import * as React from 'react';
import { useEffect, useState } from 'react';
import { geoJSON } from 'leaflet';
import { FeatureGroup, LayersControl, Map as MapLeaflet, ZoomControl } from 'react-leaflet';
import { CoordinatesControl } from 'react-leaflet-coordinates';
import 'leaflet.markercluster';
import { wholeWorldBounds, } from '@topconagriculture/map-utils';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import MarkerClusterGroup, { markerClusterField } from './plugins/MarkerClusterGroup/MarkerClusterGroup';
import MapMarker from './plugins/MapMarker';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { createStyles } from '@material-ui/core';
import { useDashboardConfig } from '../../../hooks/useDashboardContext';
import { makeStyles } from '@material-ui/styles';
const useStyles = makeStyles((theme) => createStyles({
    map: {
        borderRadius: theme.common.borderRadius.default,
        boxShadow: theme.common.shadow.default,
        display: 'flex',
        flex: 1,
        height: '100%',
        width: '100%',
    },
}));
const centerMapToGeoJSONLayers = (mapRef, layersRef) => {
    if (!layersRef) {
        return;
    }
    const mapEl = mapRef === null || mapRef === void 0 ? void 0 : mapRef.leafletElement;
    let boundsToCenter;
    const bounds = [layersRef]
        .map((ref) => ref === null || ref === void 0 ? void 0 : ref.leafletElement.getBounds())
        .reduce((previousBounds, currentBounds) => previousBounds.extend(currentBounds));
    if (bounds.isValid()) {
        boundsToCenter = bounds;
    }
    if (mapEl && boundsToCenter) {
        mapEl.fitBounds(boundsToCenter);
    }
};
const debouncedCenterMapToGeoJSONLayers = debounce(centerMapToGeoJSONLayers, 20);
const onEachFeature = (layersRef) => (feature, layer) => {
    if (!layersRef) {
        return;
    }
    layer.options.data = layer.feature;
    layersRef.leafletElement.addLayer(layer);
};
const getFeatureStyle = () => (feature) => {
    return {
        fill: true,
        fillColor: get(feature, 'properties.fillColor', '#46bc01'),
        fillOpacity: get(feature, 'properties.fillOpacity', get(feature, 'properties.opacity', 40)) / 100,
        opacity: get(feature, 'properties.opacity', 100) / 100,
        color: get(feature, 'properties.color', '#46bc01'),
        weight: get(feature, 'properties.weight', 4),
    };
};
const Map = ({ children, clusterMarkers, clusterMarkersOptions, mapOptions, markerList = [], pluginsOptions, locale, geoJSONList, }) => {
    const classes = useStyles();
    const [mapRef, setMapRef] = useState(null);
    const [layersRef, setLayersRef] = useState(null);
    const [, setMarkersRef] = useState(null);
    const { GOOGLE_MAP_VERSION } = useDashboardConfig();
    const { defaultMap } = JSON.parse(localStorage.getItem('@@map/settings') ||
        JSON.stringify({
            defaultMap: 'Satellite',
        }));
    const bounds = wholeWorldBounds;
    useEffect(() => {
        if (!mapRef || !layersRef) {
            return;
        }
        geoJSON(geoJSONList, {
            onEachFeature: (feature, layer) => onEachFeature(layersRef)(feature, layer),
            style: getFeatureStyle(),
        });
        debouncedCenterMapToGeoJSONLayers(mapRef, layersRef);
        mapRef.leafletElement.invalidateSize();
        mapRef.leafletElement.on('baselayerchange', (e) => {
            localStorage.setItem('@@map/settings', JSON.stringify({
                defaultMap: e.name,
            }));
        });
    }, [mapRef, layersRef, geoJSONList]);
    const groupMarkers = new globalThis.Map();
    const markersList = [];
    markerList.forEach((marker) => {
        var _a;
        const mapMarket = React.createElement(MapMarker, { key: marker.id, marker: marker, onMarkerClick: () => null });
        marker.group = marker.group || 'default';
        if (groupMarkers.has(marker.group)) {
            (_a = groupMarkers.get(marker.group)) === null || _a === void 0 ? void 0 : _a.push(mapMarket);
        }
        else {
            groupMarkers.set(marker.group, [mapMarket]);
        }
    });
    groupMarkers.forEach((value, key) => {
        const icon = key === 'default' ? undefined : markerClusterField;
        const markerFeatureGroup = (React.createElement(FeatureGroup, { key: key, ref: setMarkersRef }, clusterMarkers ? (React.createElement(MarkerClusterGroup, { showCoverageOnHover: false, ...clusterMarkersOptions, iconCreateFunction: icon }, value)) : (value)));
        markersList.push(markerFeatureGroup);
    });
    return (React.createElement(MapLeaflet, { ...mapOptions, bounds: bounds, ref: setMapRef, className: classes.map },
        children,
        React.createElement(FeatureGroup, { ref: setLayersRef }),
        markersList,
        React.createElement(CoordinatesControl, { position: "bottomright", coordinates: (pluginsOptions.cursorCoordinates && pluginsOptions.cursorCoordinates.type) || 'decimal' }),
        React.createElement(LayersControl, { position: "bottomright" },
            React.createElement(LayersControl.BaseLayer, { name: 'Satellite', va: true, checked: defaultMap === 'Satellite' },
                React.createElement(ReactLeafletGoogleLayer, { googleMapsLoaderConf: {
                        KEY: pluginsOptions.search.token,
                        LANGUAGE: locale,
                        VERSION: GOOGLE_MAP_VERSION,
                        LIBRARIES: ['places'],
                    }, type: 'satellite' })),
            React.createElement(LayersControl.BaseLayer, { name: 'Street', checked: defaultMap === 'Street' },
                React.createElement(ReactLeafletGoogleLayer, { googleMapsLoaderConf: {
                        KEY: pluginsOptions.search.token,
                        LANGUAGE: locale,
                        VERSION: GOOGLE_MAP_VERSION,
                        LIBRARIES: ['places'],
                    }, type: 'roadmap' }))),
        React.createElement(ZoomControl, { position: "bottomright" })));
};
export default Map;
//# sourceMappingURL=Map.js.map